// src/common/pages/authorValidation/AuthorValidationPage.tsx
/**
 * La validación de datos del autor, en pasos (KAN-460, KAN-462): las fotos del DNI (el frente, el
 * dorso y las dos juntas), el teléfono y el código postal (con el domicilio si no se leyó, y lo que
 * falte para una documentación), y un resumen de solo vista con «Confirmar mis datos» y «Modificar
 * mis datos». Reemplaza, para AuthorComposer, al formulario genérico (se registra en
 * externalValidationPages.tsx).
 *
 * Lo que lee este navegador completa los datos; no los certifica. Qué dato lleva el tic lo decide
 * el server, que lee la misma foto al recibirla (POST /public/dni-upload) y la compara con lo
 * confirmado al cerrar la solicitud. Por eso la foto se entrega al confirmar el resumen, antes de
 * enviar los datos.
 *
 * "No puedo usar mi DNI" lleva a cargar todo a mano, sin foto y sin tics, y termina en el mismo
 * resumen. El seudónimo y el IPI no están en esta pantalla (KAN-458): quedan para la editorial. Al
 * pie, siempre, el enlace a cómo se usan los datos.
 *
 * En cada cambio de paso el foco va al título del paso nuevo, y se hace una sola cosa a la vez:
 * entregar la foto o enviar los datos.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Link, MobileStepper, Typography } from "@mui/material";
import { useLecturaDni } from "../dniUpload/useLecturaDni";
import { ArrowBackIcon } from "../../../icons";
import { textosDni } from "../dniUpload/textos";
import { textosPagina } from "./textos";
import { PasoFotos, caraAPedir, vistaDeLasFotos } from "./PasoFoto";
import { PasoConfirmacion } from "./PasoConfirmacion";
import { FormularioDeCampos, valoresParaCorregir } from "./FormularioDeCampos";
import { UsoDeDatos } from "./UsoDeDatos";
import {
  CAMPOS_DEL_RESUMEN,
  CAMPOS_PARA_DOCUMENTACION,
  FUERA_DE_LA_PANTALLA,
  PISO_Y_DEPARTAMENTO,
  armarEnvio,
  camposDelPasoTelefono,
  dadosVigentes,
  domicilioReemplazado,
  estaVacio,
  etiquetaDeCampo,
  filasDeConfirmacion,
  sinBorrarLaLocalidad,
  soloCampos,
  valoresDesdeLectura,
  valoresGuardados,
  valoresQueViajan,
} from "./datos";
import type { TipoDelPasoTelefono } from "./datos";
import type { AuthorValidationPageProps, CampoConfig, ResultadoDelEnvio, Valores } from "./tipos";

const BASE_URL = process.env.REACT_APP_API_URL?.replace("/graphql", "") || "http://localhost:4000";

/**
 * Los pasos: las fotos (el frente, el dorso y las dos juntas, según la lectura: vistaDeLasFotos), el
 * teléfono y el código postal, y el resumen. Aparte, la carga a mano y su resumen.
 */
type Paso = "fotos" | "telefono" | "resumen" | "manual" | "resumenManual";
/** Los puntos del indicador: el frente, el dorso (y las dos fotos juntas), el teléfono y el resumen. */
const TOTAL_DE_PASOS = 4;

/** Fuera de la vista y presente para un lector de pantalla. */
const SOLO_PARA_LECTORES = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

/**
 * Los rechazos del server que la pantalla explica con su propio texto. La ruta pública
 * (emailVerification.routes.ts, generada) los manda como `validation_failed` sin `field`:
 *  - el número de documento que otro autor ya tiene (índice único): `meta.target` con el campo;
 *  - el bloqueo por documentación en preparación de una edición (editionDocumentationLock.ts),
 *    que no deja cambiar los datos de un autor que la documentación usa.
 */
const CODIGO_DUPLICADO = "P2002";
const CODIGO_BLOQUEO = "ENTITY_LOCKED_BY_EDITION_DOCUMENTATION";
/**
 * Los códigos con que el server rechaza un campo. El `message` del server está en castellano y
 * la pantalla es es/en/pt: el texto lo escribe el cliente según el código (ver `textoDelCampo`).
 */
const CODIGO_REQUERIDO = "VALIDATION_REQUIRED";
const CODIGO_OPCION_INVALIDA = "VALIDATION_INVALID_OPTION";
const CODIGO_IDENTIFICADOR_INVALIDO = "IDENTIFIER_INVALID";
/**
 * Un enlace que ya no vale, además del vencido: revocado (410), inexistente (404) o con la firma
 * mala (400), según validatePublicRequest (emailVerification.routes.ts). También uno que dejó de
 * pedir los datos: después se mandó al mismo email un enlace de solo email (400
 * no_validation_context), o cambió lo que pide la verificación mientras se enviaba el formulario
 * (409 scope_changed). Reintentar no sirve nunca: como con el vencido, hay que pedir uno nuevo. Se
 * dice con e_link, el texto que da la entrega de la foto con esos mismos códigos (useLecturaDni.ts);
 * el vencido, con e_link_expired, también el de la entrega. En esta pantalla, un solo texto para
 * cada caso.
 */
const ENLACE_SIN_VALIDEZ = new Set([
  "revoked",
  "not_found",
  "invalid_signature",
  "no_validation_context",
  "scope_changed",
]);

/** El resumen de la carga a mano muestra también el email, que ese formulario deja cambiar. */
const CAMPOS_DEL_RESUMEN_A_MANO: readonly string[] = [...CAMPOS_DEL_RESUMEN, "email"];

/**
 * Al modificar el resumen o el domicilio del paso del teléfono, otra calle, provincia, localidad o
 * país es otro domicilio: el código postal, el piso y el departamento son del viejo. Se vacían ahí
 * mismo, con lo de antes debajo, y el código postal, obligatorio, se vuelve a cargar (KAN-456). Así
 * no viaja un código postal viejo con una calle nueva.
 */
const SI_CAMBIA_EL_DOMICILIO = {
  si: ["streetAndNumber", "province", "city", "country"],
  vaciar: ["postalCode", "floor", "suite"],
} as const;

/** Lo que el resumen exige al modificar: lo de la documentación y el teléfono (KAN-456). */
const OBLIGATORIOS_DEL_RESUMEN: readonly string[] = [...CAMPOS_PARA_DOCUMENTACION, "phone"];

export const AuthorValidationPage: React.FC<AuthorValidationPageProps> = ({
  vid,
  sig,
  info,
  tenantInfo,
  language,
  onSubmitted,
}) => {
  const textos = textosPagina(language);
  const T = textosDni(language);
  const catalogos = useMemo(() => info.lookups ?? {}, [info.lookups]);
  const porNombre = useMemo(() => new Map(info.fields.map((c) => [c.name, c] as const)), [info.fields]);
  const guardados = useMemo(() => valoresGuardados(info.entityData, info.fields), [info.entityData, info.fields]);
  // Lo que el autor puede editar en esta pantalla: lo del registro, sin el seudónimo ni el IPI (KAN-458).
  const editables = useMemo(
    () => info.editableFields.filter((n) => !FUERA_DE_LA_PANTALLA.includes(n)),
    [info.editableFields]
  );
  const camposDe = (nombres: readonly string[]): CampoConfig[] =>
    nombres
      .filter((n) => editables.includes(n))
      .map((n) => porNombre.get(n))
      .filter((c): c is CampoConfig => !!c);
  const esEditable = (nombre: string) => editables.includes(nombre) && porNombre.has(nombre);
  // Quién pide los datos, para los textos de los rechazos; sin nombre, «quien le envió este enlace».
  const editora = tenantInfo.tenantName?.trim() ?? "";
  const conEditora = (con: string, sin: string) => (editora ? con.replace(/\{editora\}/g, () => editora) : sin);

  const [paso, setPaso] = useState<Paso>("fotos");
  // Lo leído del documento, al salir de las fotos (valoresDesdeLectura).
  const [leidos, setLeidos] = useState<Valores>({});
  // El domicilio leído que el catálogo no ubicó, entero: ayuda donde se pide el domicilio.
  const [domicilioLeido, setDomicilioLeido] = useState("");
  // Lo que el autor dio a mano en el paso del teléfono: cuenta como dado y no se vuelve a pedir, ni
  // al volver a pasar por las fotos, salvo en los campos que una foto nueva cambió (dadosVigentes).
  const [dados, setDados] = useState<Valores>({});
  // Lo leído cuando el autor dio esos datos: contra eso se ve si una foto nueva los cambió.
  const leidosAlDar = useRef<Valores>({});
  // Lo que pide el paso del teléfono: se calcula al llegar desde las fotos, y al volver desde el
  // resumen se ve igual, con lo que el autor escribió.
  const [pasoTelefono, setPasoTelefono] = useState<{
    campos: string[];
    obligatorios: string[];
    tipo: TipoDelPasoTelefono;
  }>({ campos: [], obligatorios: [], tipo: "telefono" });
  // Si el resumen se está modificando, ahí mismo.
  const [modificando, setModificando] = useState(false);
  // Lo cargado a mano, para su resumen y para volver al formulario con lo escrito.
  const [valoresAMano, setValoresAMano] = useState<Valores | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [entregando, setEntregando] = useState(false);
  const [error, setError] = useState("");
  const [erroresCampo, setErroresCampo] = useState<Record<string, string>>({});
  // Si la entrega de la foto falló. Es de la pantalla y no el sendError del hook, que dura hasta la
  // próxima entrega: al volver a las fotos y llegar otra vez al resumen no queda a la vista. El
  // reintento manda lo que se ve (ver PasoConfirmacion).
  const [entregaFallida, setEntregaFallida] = useState(false);
  // Los campos que el server rechazó y el resumen no muestra: se suman al formulario de modificar
  // para que el autor pueda corregirlos, y quedan aunque al reenviar se limpien los errores.
  const [camposSumados, setCamposSumados] = useState<string[]>([]);
  // Si el último rechazo no dejó un campo para corregir: ahí se ofrece la salida a la carga a mano,
  // también con la foto ya entregada. Se limpia al salir de las fotos y al llegar al resumen, así un
  // rechazo en otro paso no la abre.
  const [sinCampoParaCorregir, setSinCampoParaCorregir] = useState(false);

  const lectura = useLecturaDni({
    open: true,
    destino: { tipo: "publico", vid, sig },
    author: {
      firstName: guardados.firstName ?? "",
      lastName: guardados.lastName ?? "",
      taxId: guardados.taxId ?? "",
      taxIdType: guardados.taxIdType ?? "",
      dateOfBirth: guardados.dateOfBirth ?? "",
      gender: guardados.gender ?? "",
    },
    // Los catálogos por entidad (Country, ProvinceState): sin ellos el domicilio leído no se reparte.
    lookups: info.lookups,
    // El domicilio del documento se propone siempre: el autor lo confirma o lo corrige.
    domicilioCargado: false,
    onUploaded: () => undefined,
    idioma: language,
  });

  // Qué se ve de las fotos lo dice la lectura. Mientras se procesa una foto se sostiene lo que se
  // veía: la cara que se reemplaza ya se borró, y el título no tiene que saltar durante la espera.
  const vistaCalculada = vistaDeLasFotos(lectura);
  const vistaSostenida = useRef(vistaCalculada);
  if (!lectura.procesando) vistaSostenida.current = vistaCalculada;
  const vistaFotos = lectura.procesando ? vistaSostenida.current : vistaCalculada;

  // El foco va al título del paso nuevo, también al cambiar lo que se ve de las fotos (aprobado el
  // frente, se pide el dorso): el botón que lo tenía ya no está, y quien usa un lector de pantalla
  // oye dónde está. Al título y no al primer campo, que en un teléfono abriría el teclado. Al abrir
  // la pantalla no se mueve: se compara con el anterior, que aguanta el doble montaje de StrictMode.
  // El título de cada paso es su primer h2.
  const claveDelPaso = paso === "fotos" ? `fotos:${vistaFotos}` : paso;
  const zonaDelPaso = useRef<HTMLDivElement>(null);
  const pasoAnterior = useRef(claveDelPaso);
  useEffect(() => {
    if (claveDelPaso === pasoAnterior.current) return;
    pasoAnterior.current = claveDelPaso;
    zonaDelPaso.current?.querySelector<HTMLElement>("h2")?.focus();
  }, [claveDelPaso]);

  const limpiarAvisos = () => {
    setError("");
    setErroresCampo({});
    setEntregaFallida(false);
  };

  /**
   * De las fotos al teléfono: se lee lo que dio el documento, y lo dado a mano antes sigue salvo
   * donde la foto nueva cambió algo. El domicilio se pide si no se leyó de ninguna cara del DNI
   * (recorrido del usuario: con dos dorsos ninguno lo trajo, y el guardado tapaba el faltante) y
   * el autor no lo escribió antes.
   */
  const irAlTelefono = () => {
    limpiarAvisos();
    setSinCampoParaCorregir(false);
    const leido = lectura.valoresLeidos();
    const nuevos = valoresDesdeLectura(leido, guardados, catalogos);
    const vigentes = dadosVigentes(dados, leidosAlDar.current, nuevos);
    leidosAlDar.current = nuevos;
    const domicilioDelDni = !estaVacio(leido.valores.streetAndNumber) || !!leido.domicilioSinRepartir;
    setLeidos(nuevos);
    setDados(vigentes);
    setDomicilioLeido(leido.domicilioSinRepartir ?? "");
    setPasoTelefono(
      camposDelPasoTelefono({ ...guardados, ...nuevos, ...vigentes }, guardados, {
        pedirDomicilio: !domicilioDelDni && !("streetAndNumber" in vigentes),
      })
    );
    setPaso("telefono");
  };

  // Una cosa a la vez: entregar la foto o enviar los datos. `ocupado` deshabilita los botones,
  // pero recién cuando la pantalla se vuelve a dibujar; el candado no espera a eso, y un segundo
  // toque llegado antes no repite la entrega. handleSend no se cuida solo: en el diálogo de la
  // aplicación lo frena canSend, y acá se lo llama por código.
  const enCurso = useRef(false);
  const unaPorVez = async (tarea: () => Promise<void>) => {
    if (enCurso.current) return;
    enCurso.current = true;
    setOcupado(true);
    try {
      await tarea();
    } finally {
      enCurso.current = false;
      setOcupado(false);
    }
  };

  /**
   * El campo de un rechazo, solo si el autor lo puede editar en este formulario: el que manda el
   * server o, si no lo manda (un índice único lo dice en `meta.target`), el primero de esos. Un
   * campo que el autor no puede editar en esta pantalla (una renovación, las editoriales, el
   * seudónimo y el IPI) no cuenta: el rechazo va sin campo, con el texto del cliente y la salida a
   * la carga a mano.
   */
  const campoDelRechazo = (data: { field?: unknown; meta?: { target?: unknown } | null }): string | undefined => {
    if (typeof data.field === "string" && esEditable(data.field)) return data.field;
    const objetivo = data.meta?.target;
    if (!Array.isArray(objetivo)) return undefined;
    return objetivo.find((n): n is string => typeof n === "string" && esEditable(n));
  };

  /** El texto de un rechazo sobre un campo, por código: nunca el `message` del server. */
  const textoDelCampo = (codigo: unknown): string => {
    switch (codigo) {
      case CODIGO_DUPLICADO:
        return conEditora(textos.rechazoDuplicado, textos.rechazoDuplicadoSinEditora);
      case CODIGO_REQUERIDO:
        return textos.requerido;
      case CODIGO_OPCION_INVALIDA:
        return textos.rechazoOpcionInvalida;
      case CODIGO_IDENTIFICADOR_INVALIDO:
        return textos.rechazoDatoInvalido;
      default:
        return textos.rechazoRevise;
    }
  };

  const enviarDatos = async (valores: Valores) => {
    setError("");
    setErroresCampo({});
    try {
      const respuesta = await fetch(`${BASE_URL}/api/public/external-validation/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vid, sig, data: armarEnvio(editables, valores, info.fields, info.entityData) }),
      });
      const data = await respuesta.json();
      if (data.success) {
        // El mensaje final lo escribe la pantalla, en el idioma del autor y sin la cuenta de cambios
        // (KAN-457), con el nombre que trae la respuesta o, si no, el de la solicitud. Un segundo
        // envío (already_verified) recibe el mismo.
        const nombre = (typeof data.tenantInfo?.tenantName === "string" ? data.tenantInfo.tenantName.trim() : "") || editora;
        const resultado: ResultadoDelEnvio = {
          changes: Number(data.changes) || 0,
          ...(data.tenantInfo ? { tenantInfo: data.tenantInfo } : {}),
          confirmation: {
            title: textos.confirmacionTitulo,
            message: nombre
              ? textos.confirmacionMensaje.replace(/\{editora\}/g, () => nombre)
              : textos.confirmacionMensajeSinEditora,
          },
        };
        onSubmitted(resultado);
        return;
      }
      if (data.error === "validation_failed") {
        const campo = campoDelRechazo(data);
        setSinCampoParaCorregir(!campo);
        // El texto es siempre del cliente, nunca el `message` del server: sin campo o con el bloqueo,
        // el de arriba; con un campo, el que le toca a su código (textoDelCampo).
        if (data.code === CODIGO_BLOQUEO) {
          setError(conEditora(textos.rechazoBloqueo, textos.rechazoBloqueoSinEditora));
          return;
        }
        if (!campo) {
          setError(conEditora(textos.rechazoSinCampo, textos.rechazoSinCampoSinEditora));
          return;
        }
        const mensaje = textoDelCampo(data.code);
        setErroresCampo({ [campo]: mensaje });
        setError(`${etiquetaDeCampo(porNombre.get(campo), language) || campo}: ${mensaje}`);
        // El campo se corrige donde está: en el resumen, que pasa a modificar (con el campo sumado
        // si no es uno de los que muestra: un dato que el autor no ve ahí), o en la carga a mano.
        if (paso === "resumen") {
          if (!(CAMPOS_DEL_RESUMEN as readonly string[]).includes(campo)) {
            setCamposSumados((previos) => (previos.includes(campo) ? previos : [...previos, campo]));
          }
          setModificando(true);
        } else if (paso === "resumenManual") {
          setPaso("manual");
        }
        return;
      }
      if (data.error === "expired") setError(T.e_link_expired);
      else if (ENLACE_SIN_VALIDEZ.has(data.error)) setError(T.e_link);
      else setError(textos.errorGenerico);
    } catch {
      setError(textos.errorDeRed);
    }
  };


  /**
   * «Confirmar mis datos»: con el DNI, se entrega la foto y se envían los datos. La foto va una sola
   * vez: si ya llegó (y lo que falló después fue el envío de los datos), no se repite; si la
   * lectura la está entregando, se espera. La carga a mano envía directo.
   */
  const confirmar = async (valores: Valores, conFoto: boolean) => {
    if (lectura.sending) return;
    await unaPorVez(async () => {
      setError("");
      setEntregaFallida(false);
      if (conFoto && !lectura.enviado) {
        // handleSend devuelve false también sin intentarlo, cuando falta una foto, y entonces no
        // hay motivo que mostrar. Las fotos exigen las dos, así que no debería faltar; si falta, o
        // si una cara dejó de cumplir su regla o la aceptación de las dos (caraAPedir), se vuelve a
        // las fotos, que la piden. Así un false es siempre una entrega que falló, y su motivo está
        // en lectura.sendError, que se muestra con «Reintentar».
        if (!lectura.anversoEnviable || !lectura.reversoEnviable || caraAPedir(lectura)) {
          setPaso("fotos");
          return;
        }
        // La respuesta espera la lectura de la foto en el server, que tarda hasta 25 s; con la
        // subida, la espera total puede llegar a los 120 s del plazo de la entrega
        // (PLAZO_DE_ENTREGA_MS): se avisa.
        setEntregando(true);
        let entregada = false;
        try {
          entregada = await lectura.handleSend({ sinAviso: true });
        } finally {
          setEntregando(false);
        }
        if (!entregada) {
          setEntregaFallida(true);
          return;
        }
      }
      await enviarDatos(valores);
    });
  };

  const entregandoFoto = entregando || lectura.sending;

  // El paso del teléfono. El piso y el departamento que vació el domicilio leído del DNI son del
  // domicilio viejo: se ofrecen vacíos, con lo que había debajo («Antes: 3»). El autor decide a la
  // vista, y lo que deje vacío viaja vacío (KAN-456).
  const conLoLeidoYLoDado: Valores = { ...leidos, ...dados };
  const ayudasDelTelefono: Record<string, string> = {};
  for (const nombre of PISO_Y_DEPARTAMENTO) {
    const vaciado = Object.prototype.hasOwnProperty.call(conLoLeidoYLoDado, nombre) && estaVacio(conLoLeidoYLoDado[nombre]);
    if (pasoTelefono.campos.includes(nombre) && vaciado && !estaVacio(guardados[nombre])) {
      ayudasDelTelefono[nombre] = textos.antes.replace("{valor}", () => guardados[nombre]);
    }
  }
  const tituloDelTelefono = {
    telefono: textos.pasoTelefono,
    domicilio: textos.pasoTelefonoYDomicilio,
    faltantes: textos.pasoDatosQueFaltan,
  }[pasoTelefono.tipo];

  // El resumen: lo que va a quedar (lo guardado, con lo leído y lo dado a mano encima). Al modificar,
  // los mismos datos y los que el server rechazó sin estar a la vista.
  const valoresDelResumen: Valores = { ...guardados, ...leidos, ...dados };
  const camposDelResumen = camposDe([
    ...CAMPOS_DEL_RESUMEN,
    ...camposSumados.filter((n) => !(CAMPOS_DEL_RESUMEN as readonly string[]).includes(n)),
  ]);
  // Un DU guardado con puntos se modifica en dígitos; sin tocarlo, no viaja (armarEnvio).
  const valoresParaModificar = valoresParaCorregir(camposDelResumen, valoresDelResumen);
  const camposAMano = camposDe(editables);

  const punto =
    paso === "fotos" ? (vistaFotos === "anverso" ? 0 : 1) : paso === "telefono" ? 2 : paso === "resumen" ? 3 : -1;

  /**
   * «Volver», hasta el envío final: del teléfono a las fotos, del resumen al teléfono, de modificar
   * el resumen a verlo, y del resumen de la carga a mano a su formulario. De las fotos y de la
   * carga a mano no se vuelve.
   */
  const volver: (() => void) | null =
    paso === "telefono"
      ? () => setPaso("fotos")
      : paso === "resumen"
      ? modificando
        ? () => setModificando(false)
        : () => setPaso("telefono")
      : paso === "resumenManual"
      ? () => setPaso("manual")
      : null;

  /**
   * El contenido del título de un paso. Los puntos del indicador solo se ven: en los pasos con
   * puntos, el título empieza con «Paso N de 4: », fuera de la vista. Va en el mismo encabezado que
   * recibe el foco al cambiar de paso, así quien usa un lector de pantalla lo oye al llegar, y
   * también al recorrer los encabezados.
   */
  const encabezado = (texto: string): React.ReactNode =>
    punto >= 0 ? (
      <>
        <Box component="span" sx={SOLO_PARA_LECTORES}>
          {`${textos.pasoDeTotal.replace("{actual}", String(punto + 1)).replace("{total}", String(TOTAL_DE_PASOS))}: `}
        </Box>
        {texto}
      </>
    ) : (
      texto
    );

  const titulo = (texto: string) => (
    // tabIndex -1: recibe el foco al llegar al paso.
    <Typography tabIndex={-1} variant="h6" component="h2" sx={{ mb: 2 }}>
      {encabezado(texto)}
    </Typography>
  );

  const irAMano = () => {
    limpiarAvisos();
    setModificando(false);
    setPaso("manual");
  };

  /** Las salidas entre el DNI y la carga a mano (ver el pie). */
  const enAMano = paso === "manual" || paso === "resumenManual";
  const salidaAMano = !enAMano && (paso === "fotos" || !lectura.enviado || sinCampoParaCorregir);

  return (
    <Box sx={{ textAlign: "left" }}>
      {/* El encabezado principal: quien pide los datos, como en el formulario genérico. VerifyEmailPage
          dibuja arriba solo el logo. */}
      {tenantInfo.tenantName && (
        <Typography variant="h6" component="h1" sx={{ textAlign: "center", mb: 2 }}>
          {tenantInfo.tenantName}
        </Typography>
      )}

      {/* Los puntos solo se ven: el paso, para el lector de pantalla, va en el título (ver encabezado).
          Sin el recuadro que les pone el tema y 50 % más grandes que los 8 px de MUI (KAN-454). */}
      {punto >= 0 && (
        <MobileStepper
          variant="dots"
          steps={TOTAL_DE_PASOS}
          activeStep={punto}
          position="static"
          backButton={null}
          nextButton={null}
          sx={{
            justifyContent: "center",
            // Con más especificidad que el tema, que les da a los Paper su borde y al indicador un fondo
            // claro con el atajo `background`, que pisaría a `bgcolor`.
            "&.MuiPaper-root": { borderStyle: "none", borderWidth: 0, background: "transparent" },
            p: 0,
            mb: 2,
          }}
          // El tamaño, en cada punto: así no compite con la regla del punto de MUI.
          slotProps={{ dot: { style: { width: 12, height: 12 } } }}
        />
      )}

      {/* Hasta el envío final, cada paso vuelve al anterior; no mientras algo está en curso. */}
      {volver && (
        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            // La flecha del set de íconos del proyecto; decorativa (SvgIcon la oculta al lector de pantalla).
            startIcon={<ArrowBackIcon aria-hidden="true" />}
            disabled={ocupado || lectura.sending || lectura.procesando}
            onClick={() => {
              limpiarAvisos();
              volver();
            }}
          >
            {textos.volver}
          </Button>
        </Box>
      )}

      <Box ref={zonaDelPaso}>
        {paso === "fotos" && (
          <PasoFotos
            vista={vistaFotos}
            titulo={encabezado(
              vistaFotos === "anverso" ? textos.pasoFrente : vistaFotos === "reverso" ? textos.pasoDorso : textos.pasoFotos
            )}
            lectura={lectura}
            textos={textos}
            T={T}
            onContinuar={irAlTelefono}
          />
        )}

        {paso === "telefono" && (
          <>
            {titulo(tituloDelTelefono)}
            {/* El domicilio leído que el catálogo no ubicó, como ayuda, si el paso pide la calle. */}
            {domicilioLeido && pasoTelefono.campos.includes("streetAndNumber") && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {textos.segunSuDni.replace("{texto}", () => domicilioLeido)}
              </Typography>
            )}
            <FormularioDeCampos
              campos={camposDe(pasoTelefono.campos)}
              obligatorios={pasoTelefono.obligatorios}
              // Precargado con lo guardado, lo leído y lo ya dado; con el tipo de documento aunque no
              // se muestre: la máscara del número depende de él.
              valoresIniciales={{ ...guardados, ...leidos, ...dados }}
              ayudas={ayudasDelTelefono}
              catalogos={catalogos}
              idioma={language}
              textos={textos}
              textoBoton={textos.continuar}
              textoEspera={textos.enviandoDatos}
              ocupado={ocupado}
              erroresDelServidor={erroresCampo}
              // Lo que el autor no cambia frente a lo guardado crudo no se valida (sinCambiar).
              guardado={info.entityData}
              // Si pide la calle, precargada con lo guardado: otro domicilio vacía el código postal,
              // el piso y el depto., como en el resumen.
              vaciarAlCambiar={{ ...SI_CAMBIA_EL_DOMICILIO, textoAntes: textos.antes }}
              onEnviar={(v) => {
                limpiarAvisos();
                setDados((previos) => ({ ...previos, ...soloCampos(v, pasoTelefono.campos) }));
                leidosAlDar.current = leidos;
                setModificando(false);
                setSinCampoParaCorregir(false);
                setPaso("resumen");
              }}
            />
          </>
        )}

        {paso === "resumen" && (
          <>
            <PasoConfirmacion
              titulo={encabezado(textos.pasoConfirmar)}
              filas={filasDeConfirmacion(valoresDelResumen, info.fields, catalogos, language)}
              aviso={(() => {
                const reemplazado = domicilioReemplazado(leidos, guardados, info.fields, catalogos, language);
                return reemplazado ? textos.domicilioReemplaza.replace("{domicilio}", () => reemplazado) : undefined;
              })()}
              modificando={modificando}
              campos={camposDelResumen}
              obligatorios={OBLIGATORIOS_DEL_RESUMEN}
              vaciarAlCambiar={{ ...SI_CAMBIA_EL_DOMICILIO, textoAntes: textos.antes }}
              valores={valoresParaModificar}
              catalogos={catalogos}
              idioma={language}
              textos={textos}
              ocupado={ocupado || lectura.sending}
              textoEspera={entregandoFoto ? textos.entregandoFoto : textos.enviandoDatos}
              erroresDelServidor={erroresCampo}
              // Lo que el autor no cambia frente a lo guardado crudo no se valida (sinCambiar).
              guardado={info.entityData}
              // El motivo lo dice el hook; sin motivo no debería quedar (ver confirmar).
              fallaDeEntrega={entregaFallida && !ocupado ? lectura.sendError || T.upload_failed : undefined}
              onConfirmar={() => confirmar(valoresQueViajan(leidos, dados, guardados), true)}
              // El aviso de una entrega que falló queda, sin «Reintentar» (ver PasoConfirmacion).
              onModificar={() => setModificando(true)}
              onModificados={(v) => {
                // Viaja lo que el autor cambió, con lo leído y lo dado antes, como con «Confirmar mis
                // datos». Lo que escribe cuenta como dado: no se le vuelve a pedir. Lo guardado que
                // vio y no tocó no viaja (recorrido 4), y si el autor no tocó la localidad, la
                // guardada no se borra (sinBorrarLaLocalidad).
                const tocados = Object.fromEntries(
                  Object.entries(v).filter(([campo, valor]) => String(valor ?? "") !== String(valoresParaModificar[campo] ?? ""))
                );
                const envio = valoresQueViajan(leidos, { ...dados, ...tocados }, guardados);
                confirmar("city" in tocados ? envio : sinBorrarLaLocalidad(envio, guardados), true);
              }}
            />
            {/* La región ya está antes de la espera: así un lector de pantalla la anuncia al aparecer. */}
            <Box role="status">
              {entregandoFoto && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
                  <CircularProgress size={20} aria-label={textos.entregandoFoto} />
                  <Typography variant="body2">{textos.entregandoFoto}</Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {paso === "manual" && (
          <>
            {titulo(textos.pasoManual)}
            <FormularioDeCampos
              campos={camposAMano}
              // El teléfono, obligatorio solo en esta pantalla (KAN-456): el modelo lo deja opcional.
              obligatorios={[...CAMPOS_PARA_DOCUMENTACION, "email", "phone"]}
              // Un DU guardado con puntos se corrige en dígitos; sin tocarlo, no viaja (armarEnvio).
              // Al volver del resumen, con lo que el autor escribió.
              valoresIniciales={valoresAMano ?? valoresParaCorregir(camposAMano, guardados)}
              catalogos={catalogos}
              idioma={language}
              textos={textos}
              textoBoton={textos.continuar}
              textoEspera={textos.enviandoDatos}
              ocupado={ocupado}
              erroresDelServidor={erroresCampo}
              // Lo que el autor no cambia frente a lo guardado crudo no se valida (sinCambiar).
              guardado={info.entityData}
              onEnviar={(v) => {
                limpiarAvisos();
                setValoresAMano(soloCampos(v, camposAMano.map((c) => c.name)));
                setPaso("resumenManual");
              }}
            />
          </>
        )}

        {paso === "resumenManual" && valoresAMano && (
          <PasoConfirmacion
            titulo={encabezado(textos.pasoConfirmar)}
            filas={filasDeConfirmacion({ ...guardados, ...valoresAMano }, info.fields, catalogos, language, CAMPOS_DEL_RESUMEN_A_MANO)}
            // Modificar es volver al formulario de la carga a mano, con lo escrito.
            modificando={false}
            campos={[]}
            obligatorios={[]}
            valores={{}}
            catalogos={catalogos}
            idioma={language}
            textos={textos}
            ocupado={ocupado}
            textoEspera={textos.enviandoDatos}
            onConfirmar={() => confirmar(valoresAMano, false)}
            onModificar={() => {
              limpiarAvisos();
              setPaso("manual");
            }}
            onModificados={() => undefined}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Las salidas entre el DNI y la carga a mano, no mientras algo está en curso: una entrega que
          termina después movería al autor de donde fue, o enviaría los datos. Con la foto ya
          entregada, la carga a mano no ofrece volver a sacarla, y después de las fotos la salida
          se ofrece solo si el último rechazo no dejó nada que el autor pueda corregir. */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        {enAMano && !lectura.enviado ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            disabled={ocupado}
            onClick={() => {
              limpiarAvisos();
              setPaso("fotos");
            }}
          >
            {textos.usarMiDni}
          </Link>
        ) : salidaAMano ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            // Tampoco mientras se procesa una foto: la espera solo deja cancelar.
            disabled={ocupado || lectura.sending || lectura.procesando}
            onClick={irAMano}
          >
            {textos.noPuedoUsarDni}
          </Link>
        ) : (
          <span />
        )}
        <UsoDeDatos textos={textos} />
      </Box>
    </Box>
  );
};
